import { Checklist as ChecklistIcon } from 'components/icons';
import PermissionButton from 'components/buttons/PermissionButton';
import CompleteCorrespondenceModal from 'components/modals/correspondence/CompleteCorrespondenceModal';
import { useModal } from 'components/modals/useModal';
import { Tooltip } from 'components/ui/tooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    CorrespondenceDirectionType,
    CorrespondenceListElementDto,
    CorrespondenceStatusType,
} from 'types/correspondence';
import { t } from 'utils/getTranslations';

interface Props {
    correspondences: CorrespondenceListElementDto[];
}

export const isEligibleForCompletion = (c: CorrespondenceListElementDto): boolean => {
    return (
        c.directionTypeCode === CorrespondenceDirectionType.OUTGOING &&
        c.statusTypeCode === CorrespondenceStatusType.IN_PROGRESS
    );
};

export const CompleteCorrespondenceButton = ({ correspondences }: Props) => {
    const { openModal, closeModal } = useModal();
    const hasSelection = correspondences.length > 0;
    const allEligible = hasSelection && correspondences.every(isEligibleForCompletion);
    const isEnabled = allEligible;
    const showTooltip = hasSelection && !allEligible;

    const handleClick = () => {
        openModal(<CompleteCorrespondenceModal correspondences={correspondences} onClose={closeModal} />);
    };

    const tooltipKey = showTooltip
        ? 'correspondence.tooltips.cannot-complete-outgoing-in-progress'
        : 'correspondence.tooltips.complete-correspondence-button';

    const button = (
        <PermissionButton
            permissions={[
                'UPDATE.CORRESPONDENCE.ALL',
                'UPDATE.CORRESPONDENCE.MY_CASES',
                'UPDATE.CORRESPONDENCE.CREATED_BY_ME',
                'UPDATE.CORRESPONDENCE.MY_OWN',
                'UPDATE.CORRESPONDENCE.MY_TEAM_CASES',
            ]}
            variant="outlined"
            size="small"
            disabled={!isEnabled}
            onClick={handleClick}
        >
            <FormattedMessage id="correspondence.complete-correspondence" />
        </PermissionButton>
    );

    return (
        <div className="flex justify-end gap-2">
            <Tooltip content={t(tooltipKey)}>
                <span>{button}</span>
            </Tooltip>
        </div>
    );
};

interface BulkSelectProps {
    correspondences: CorrespondenceListElementDto[];
    setSelectedIds: React.Dispatch<React.SetStateAction<Set<number>>>;
}

export const BulkSelectCorrespondencesButton = ({ correspondences, setSelectedIds }: BulkSelectProps) => {
    const { formatMessage } = useIntl();
    const { openModal, closeModal } = useModal();

    const eligibleCorrespondences = correspondences.filter(isEligibleForCompletion);

    const handleBulkSelect = () => {
        setSelectedIds(new Set(eligibleCorrespondences.map((c) => c.id)));
        openModal(<CompleteCorrespondenceModal correspondences={eligibleCorrespondences} onClose={closeModal} />);
    };

    return (
        <Tooltip
            content={formatMessage({ id: 'correspondence.tooltips.bulk-select-eligible-correspondences' })}
            side="bottom"
        >
            <span>
                <PermissionButton
                    permissions={[
                        'UPDATE.CORRESPONDENCE.ALL',
                        'UPDATE.CORRESPONDENCE.MY_CASES',
                        'UPDATE.CORRESPONDENCE.CREATED_BY_ME',
                        'UPDATE.CORRESPONDENCE.MY_OWN',
                        'UPDATE.CORRESPONDENCE.MY_TEAM_CASES',
                    ]}
                    variant="outlined"
                    size="small"
                    className="min-w-0 px-2"
                    aria-label={formatMessage({ id: 'correspondence.tooltips.bulk-select-eligible-correspondences' })}
                    disabled={eligibleCorrespondences.length === 0}
                    onClick={handleBulkSelect}
                >
                    <ChecklistIcon className="text-[20px]" />
                </PermissionButton>
            </span>
        </Tooltip>
    );
};
